import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getTutorEducationsOptions,
} from '../../api/generated/@tanstack/react-query.gen';
import {
  createTutorEducation,
  updateTutorEducation,
  deleteTutorEducation,
} from '../../api/generated/sdk.gen';
import type {
  TutorEducation,
  TutorEducationInput,
} from '../../api/generated/types.gen';

// Re-export generated type
export type { TutorEducation };

// Params type for backward compatibility with existing code
export interface CreateEducationParams {
  degree: string;
  course_name?: string;
  major?: string;
  university_name: string;
  year_from: number | null;
  year_to: number | null;
}

export interface UpdateEducationParams extends CreateEducationParams {
  id: string;
}

/**
 * Query hook for fetching a specific tutor's educations (for viewing another tutor's profile)
 */
export function useTutorEducationsQuery(userId: string | undefined) {
  return useQuery({
    ...getTutorEducationsOptions({ path: { userId: userId! } }),
    select: (data) => data.educations,
    enabled: !!userId,
  });
}

/**
 * Query hook for fetching the current user's educations
 * Uses the same endpoint but with a different cache key pattern for "my" data
 */
export function useMyEducationsQuery(userId: string | undefined) {
  return useQuery({
    ...getTutorEducationsOptions({ path: { userId: userId! } }),
    select: (data) => data.educations,
    enabled: !!userId,
  });
}

/**
 * Mutation hook for creating a new education entry
 * Adapts the simple params interface to the generated SDK format
 */
export function useCreateEducationMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: CreateEducationParams) => {
      const { data } = await createTutorEducation({
        body: { tutor_education: params as TutorEducationInput },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: () => {
      // Invalidate all tutor education queries to refetch fresh data
      queryClient.invalidateQueries({ queryKey: [{ _id: 'getTutorEducations' }] });
    },
  });
}

/**
 * Mutation hook for updating an existing education entry
 * Adapts the simple params interface to the generated SDK format
 */
export function useUpdateEducationMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: UpdateEducationParams) => {
      const { id, ...rest } = params;
      const { data } = await updateTutorEducation({
        body: { tutor_education: rest as TutorEducationInput },
        path: { id },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: () => {
      // Invalidate all tutor education queries to refetch fresh data
      queryClient.invalidateQueries({ queryKey: [{ _id: 'getTutorEducations' }] });
    },
  });
}

/**
 * Mutation hook for deleting an education entry
 * Adapts the simple id param to the generated SDK format
 */
export function useDeleteEducationMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await deleteTutorEducation({
        path: { id },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: () => {
      // Invalidate all tutor education queries to refetch fresh data
      queryClient.invalidateQueries({ queryKey: [{ _id: 'getTutorEducations' }] });
    },
  });
}
